/**
 * Devolver la seña de una orden anulada.
 *
 * POR QUE ES UN BOTON APARTE Y NO PARTE DE ANULAR
 * -----------------------------------------------
 * El dueño decidio que anular no mueve plata sola: a veces la seña se devuelve,
 * a veces queda a cuenta de otra cosa o el cliente no vuelve. Antes no habia
 * forma de devolverla desde la orden: el egreso se cargaba suelto en la caja y
 * la cuenta del cliente seguia con el saldo a favor.
 *
 * Aca se elige la caja de donde sale y cuanto. La base (`devolver_sena_ot`,
 * 0121_9) asienta el egreso ligado a la orden y, con cliente, cancela el saldo
 * a favor. Lo que queda por devolver se muestra en la moneda de la caja, a la
 * cotizacion en que entraron las señas: es la misma cuenta que hace la base, y
 * con la de hoy el total propuesto podia pasarse de lo cobrado. Se propone la
 * moneda en que entro la seña, y si entro en las dos se dice cuanto de cada.
 */
import { useMemo, useState } from "react";
import type { Moneda } from "@tora/shared";

import { cuentaPorDefecto, type CuentaFinanciera } from "@/components/cobro/logica";
import { Importe, InputImporte } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Campo } from "@/components/ui/campo";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SelectNativo } from "@/components/ui/select-nativo";
import { aNumero, type DevolverSenaOt } from "@/lib/tipos";

import { nombreCliente, nombreOrden, type OrdenTaller } from "./logica";

export interface DialogDevolverSenaProps {
  orden: OrdenTaller | null;
  cuentas: CuentaFinanciera[];
  guardando?: boolean;
  onCerrar: () => void;
  onConfirmar: (pago: DevolverSenaOt) => void;
}

/**
 * Todo lo que se puede devolver desde una caja de esa moneda. Lo calcula la
 * base (`devolver_tope_*`) con la misma regla que asienta la devolucion: cada
 * moneda es su pozo y solo lo que pasa se convierte, a la cotizacion en que
 * entro el otro. Sin esas columnas (una fila vieja) queda la cuenta de antes:
 * las señas menos lo ya devuelto.
 */
export function quedaPorDevolver(orden: OrdenTaller, moneda: Moneda): number {
  const tope = moneda === "USD" ? orden.devolver_tope_usd : orden.devolver_tope_ars;
  const bruto =
    tope !== null && tope !== undefined
      ? aNumero(tope)
      : aNumero(moneda === "USD" ? orden.senas_usd : orden.senas_ars) -
        aNumero(moneda === "USD" ? orden.devuelto_usd : orden.devuelto_ars);
  const queda = Math.round(bruto * 100) / 100;
  return queda < 0.05 ? 0 : queda;
}

/** Lo que queda de lo que entro EN esa moneda, sin convertir nada. */
export function entroEn(orden: OrdenTaller, moneda: Moneda): number {
  const queda = aNumero(moneda === "USD" ? orden.sena_queda_usd : orden.sena_queda_ars);
  return queda < 0.05 ? 0 : Math.round(queda * 100) / 100;
}

/**
 * La caja que se propone: la de la moneda en que entro la seña. Antes era
 * siempre Efectivo en pesos, y una seña dejada en billetes de dolar se
 * devolvia en pesos con un clic, sin que nadie eligiera la moneda. Si entro
 * en las dos, arranca en pesos y propone solo lo de pesos (el resto se
 * devuelve despues desde la de dolares). El efectivo primero: es lo que el
 * cliente dejo en el mostrador.
 */
export function cajaPropuesta(
  orden: OrdenTaller,
  cuentas: CuentaFinanciera[],
): CuentaFinanciera | null {
  const activas = cuentas.filter((c) => c.activo !== false);
  if (entroEn(orden, "USD") > 0 && entroEn(orden, "ARS") === 0) {
    const dolares = activas.filter((c) => c.moneda === "USD");
    const caja = dolares.find((c) => c.tipo === "CAJA") ?? dolares[0];
    if (caja) return caja;
  }
  return cuentaPorDefecto(activas) ?? null;
}

export function DialogDevolverSena({
  orden,
  cuentas,
  guardando = false,
  onCerrar,
  onConfirmar,
}: DialogDevolverSenaProps): JSX.Element {
  return (
    <Dialog open={orden !== null} onOpenChange={(abierto) => !abierto && onCerrar()}>
      <DialogContent tamano="md">
        {orden ? (
          // El formulario vive adentro: se arma de cero cada vez que se abre.
          // Antes el estado se limpiaba al tocar «Devolver», antes de saber si
          // salio bien; si fallaba, el dialogo quedaba abierto con el total y
          // Efectivo en pesos, y el segundo clic devolvia todo.
          <FormDevolverSena
            orden={orden}
            cuentas={cuentas}
            guardando={guardando}
            onCerrar={onCerrar}
            onConfirmar={onConfirmar}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function FormDevolverSena({
  orden,
  cuentas,
  guardando,
  onCerrar,
  onConfirmar,
}: DialogDevolverSenaProps & { orden: OrdenTaller; guardando: boolean }): JSX.Element {
  const activas = useMemo(() => cuentas.filter((c) => c.activo !== false), [cuentas]);
  // Lo que la persona eligio o tipeo; `null` = todavia nada, y vale lo
  // propuesto. Asi no hace falta un efecto que precargue al abrir.
  const [cuentaElegida, setCuentaElegida] = useState<number | null>(null);
  const [importeTipeado, setImporteTipeado] = useState<number | null | undefined>(undefined);

  const cuenta = activas.find((c) => c.id === cuentaElegida) ?? cajaPropuesta(orden, activas);
  const queda = cuenta ? quedaPorDevolver(orden, cuenta.moneda) : 0;
  // Lo propio de la moneda de la caja, si hay; si no, todo convertido.
  const propio = cuenta ? entroEn(orden, cuenta.moneda) : 0;
  const propuesto = propio > 0 ? Math.min(propio, queda) : queda;
  const importe = importeTipeado === undefined ? propuesto : importeTipeado;

  const pesos = entroEn(orden, "ARS");
  const dolares = entroEn(orden, "USD");
  const hayPozos =
    orden.sena_queda_ars !== null &&
    orden.sena_queda_ars !== undefined &&
    orden.sena_queda_usd !== null &&
    orden.sena_queda_usd !== undefined;
  // Pasa de lo que entro en esa moneda: la diferencia se convierte a la
  // cotizacion del dia en que entro la otra, no a la de hoy.
  const convierte = hayPozos && importe !== null && importe > propio + 0.05;

  const pasado = importe !== null && importe > queda + 0.05;
  const puede = cuenta !== null && importe !== null && importe > 0 && !pasado;

  return (
    <>
      <DialogHeader>
        <DialogTitle>Devolver la seña</DialogTitle>
        <DialogDescription>
          {nombreOrden(orden)} · {orden.equipo} · {nombreCliente(orden)}. La orden está anulada: la
          plata sale de la caja que elijas.
        </DialogDescription>
      </DialogHeader>

      <DialogBody>
        {hayPozos && (pesos > 0 || dolares > 0) ? (
          <p className="text-sm">
            Queda por devolver{" "}
            {pesos > 0 ? <Importe monto={pesos} moneda="ARS" tamano="sm" /> : null}
            {pesos > 0 ? " de lo que entró en pesos" : null}
            {pesos > 0 && dolares > 0 ? " y " : null}
            {dolares > 0 ? <Importe monto={dolares} moneda="USD" tamano="sm" /> : null}
            {dolares > 0 ? " de lo que entró en dólares" : null}.
          </p>
        ) : null}

        <Campo label="Sale de">
          <SelectNativo
            id="devolver-cuenta"
            value={cuenta?.id ?? ""}
            onChange={(e) => {
              // Otra caja, otra moneda: vuelve a proponer lo que queda.
              setCuentaElegida(Number(e.target.value));
              setImporteTipeado(undefined);
            }}
          >
            {activas.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre} ({c.moneda})
              </option>
            ))}
          </SelectNativo>
        </Campo>

        {cuenta ? (
          <InputImporte
            label="Cuánto se devuelve"
            valor={importe}
            onValorChange={setImporteTipeado}
            moneda={cuenta.moneda}
            requerido
            ayuda="Puede ser una parte. Nunca más de lo que queda cobrado."
          />
        ) : null}

        {cuenta ? (
          <p className="text-xs text-muted-foreground">
            Desde esta caja se puede devolver hasta{" "}
            <Importe monto={queda} moneda={cuenta.moneda} tamano="sm" />, a la cotización del día en
            que entró.
            {orden.cliente_id
              ? " Se descuenta del saldo a favor del cliente."
              : " Sin cliente, queda solo en la caja y en la orden."}
          </p>
        ) : null}

        {convierte && !pasado && cuenta ? (
          <Aviso tono="warning" compacto>
            Es más de lo que entró en {cuenta.moneda === "USD" ? "dólares" : "pesos"}: la diferencia
            se convierte a la cotización del día en que entró la seña, no a la de hoy.
          </Aviso>
        ) : null}

        {pasado ? (
          <p className="text-sm text-destructive">Es más de lo que queda cobrado por esta orden.</p>
        ) : null}
      </DialogBody>

      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onCerrar}>
          Cancelar
        </Button>
        <Button
          type="button"
          variant="destructive"
          cargando={guardando}
          disabled={!puede}
          onClick={() => {
            if (!cuenta || importe === null) return;
            // Todo lo que queda viaja sin importe: la base devuelve el saldo
            // exacto y no deja centavos de redondeo colgados. El estado NO se
            // limpia aca: si la base lo rechaza, lo elegido sigue en pantalla.
            const todo = Math.abs(importe - queda) < 0.01;
            onConfirmar({ cuenta_id: cuenta.id, ...(todo ? {} : { importe }) });
          }}
        >
          Devolver
        </Button>
      </DialogFooter>
    </>
  );
}
